import FullWidthLayout from "@/layouts/FullWidthLayout";
import KSchedulesTable from "kinnect-front/KYard/Schedule/KSchedulesTable";
import { Broadcastable, FetchesData, Pageable } from "kinnect-mixins";
import { KAlertPanel, KDatePicker, KTextField } from "kinnect-general";
import {
	VCard,
	VCol,
	VExpandTransition,
	VRow,
	VSheet
} from "vuetify/lib";
import { YardSchedule } from "kinnect-models";
import { debounce, groupByKey, removeByObjectValue } from "kinnect-helpers";
import { formatDate } from "kinnect-formatters";
import { isSameDay, parseISO } from "date-fns";

export default {
	name: "YardSchedulesPage",

	metaInfo: { title: "Yard - Schedules" },

	mixins: [
		Broadcastable,
		FetchesData,
		Pageable
	],

	data: () => ( {
		searchSchedules: [],
		schedules: [],
		schedulesAlerts: [],
		search: "",
		today: ( new Date() ).setHours( 0, 0, 0, 0 ),
		actions: {
			add: {
				icon: "add_circle",
				text: "Add Schedule",
				to: { name: "yardScheduleAdd" },
				tooltip: "Add a new schedule"
			},
			request: {
				icon: "add_circle",
				text: "Request Schedule",
				to: { name: "yardScheduleRequest" },
				tooltip: "Request a new schedule"
			},
			requests: {
				badge: null,
				icon: "room_service",
				text: "Requests",
				to: { name: "yardScheduleRequests" },
				tooltip: "View schedule requests"
			}
		}
	} ),

	computed: {
		date: {
			get() {
				return this.$route.query.date;
			},

			set( date ) {
				this.$router.push( { query: { date } } );
			}
		},

		selectedDate() {
			return this.date || formatDate( this.today, "yyyy-MM-dd" );
		},

		broadcasts() {
			const broadcasts = [ {
				Model: YardSchedule,
				channel: "schedule",
				propKey: "schedules",
				eventNames: {
					c: "schedule.created",
					u: "schedule.updated",
					d: "schedule.deleted"
				}
			} ];

			if ( this.$can( "add_yard_schedules" ) ) {
				broadcasts.push( {
					channel: "schedule",
					propKey: "badge",
					propPath: "actions.requests",
					eventNames: {
						r: "schedule.requested.count"
					}
				} );
			}

			return broadcasts;
		},

		selectedDateSchedules() {
			return this.schedules.filter( schedule => {
				const isTripless = !schedule.idTrip;
				const isValidDate = !schedule.deliveryDate
					|| isSameDay( parseISO( this.selectedDate ), parseISO( schedule.deliveryDate ) );

				return isTripless && isValidDate;
			} );
		},

		displayedSchedules() {
			if ( this.search ) {
				return this.searchSchedules;
			}

			return this.selectedDateSchedules;
		},

		displayDate() {
			return formatDate( this.selectedDate );
		},

		numSchedules() {
			return this.displayedSchedules.length;
		},

		toolbarActions() {
			if ( this.$can( "add_yard_schedules" ) ) {
				return [
					this.actions.add,
					this.actions.requests
				];
			}

			return this.actions.request;
		},

		alertsByType() {
			if ( this.schedulesAlerts ) {
				return groupByKey( this.schedulesAlerts, "alertType" );
			}
		},

		_genOldSchedulesAlertTable() {
			if ( this.alertsByType && this.alertsByType.oldSchedule ) {
				return <KAlertPanel>
					<template slot="header">
						{this.alertsByType.oldSchedule.length === 1
							? "1 schedule has not been assigned to a trip"
							: `${this.alertsByType.oldSchedule.length} schedules have not been assigned to trips`
						}
					</template>

					<KSchedulesTable
						dense
						items={this.alertsByType.oldSchedule}
						onScheduleRemoved={this.onScheduleAlertRemoved}
					></KSchedulesTable>
				</KAlertPanel>;
			}
		},

		_genLateRentalPickupsAlertTable() {
			if ( this.alertsByType && this.alertsByType.lateRentalPickup ) {
				return <KAlertPanel>
					<template slot="header">
						{this.alertsByType.lateRentalPickup.length === 1
							? "1 rental pickup schedule was created over 24 hours ago and has not been completed"
							: `${this.alertsByType.lateRentalPickup.length} rental pickup schedules were created over 24 hours ago and have not been completed`
						}
					</template>

					<KSchedulesTable
						dense
						items={this.alertsByType.lateRentalPickup}
						onScheduleRemoved={this.onScheduleRemoved}
					></KSchedulesTable>
				</KAlertPanel>;
			}
		},

		genAlertTables() {
			return [
				<VExpandTransition>
					{this._genOldSchedulesAlertTable}
				</VExpandTransition>,

				<VExpandTransition>
					{this._genLateRentalPickupsAlertTable}
				</VExpandTransition>
			];
		}
	},

	watch: {
		selectedDate() {
			this.fetchSchedules();
		},

		numSchedules( numSchedules ) {
			this.$store.dispatch( "breadcrumbs/setDetails", `${numSchedules} schedules` );
		},

		search() {
			this.fetchSearchResults();
		}
	},

	methods: {
		onScheduleRemoved( schedule ) {
			this.schedules = removeByObjectValue( this.schedules, "id", schedule.id );
			this.fetchSchedulesAlerts();
		},

		onScheduleAlertRemoved( schedule ) {
			this.schedulesAlerts = removeByObjectValue( this.schedulesAlerts, "id", schedule.id );
			this.fetchSchedulesAlerts();
		},

		fetchSearchResults: debounce( async function() {
			if ( this.search ) {
				const queryWhenCallStarted = this.search;

				const schedules = await YardSchedule
				.custom( "search/schedules" )
				.params( { query: queryWhenCallStarted } )
				.get();

				if ( this.search === queryWhenCallStarted ) {
					this.searchSchedules = schedules;
				}
			} else {
				this.searchSchedules = [];
			}
		}, 500 ),

		async fetchSchedules() {
			const dateWhenCallStarted = this.selectedDate;
			const schedules = await YardSchedule
			.where( "without_trips", true )
			.where( "for_date", dateWhenCallStarted )
			.where( "approved", true )
			.get();

			if ( this.selectedDate === dateWhenCallStarted ) {
				this.schedules = schedules;
			}
		},

		async fetchSchedulesAlerts() {
			if ( this.$can( "edit_yard_schedules" ) ) {
				this.schedulesAlerts = await YardSchedule.custom( "yard/schedules/alerts" ).get();
			}
		},

		async fetchNumRequests() {
			if ( this.$can( "add_yard_schedules" ) ) {
				this.actions.requests.badge = await this.$http.get( "yard/schedules/requests/count" ).then( resp => resp.data );
			}
		},

		async fetch() {
			await this.$http.all( [
				this.fetchSchedules(),
				this.fetchSchedulesAlerts(),
				this.fetchNumRequests()
			] );
		}
	},

	render() {
		return <FullWidthLayout>
			{this.genAlertTables}

			<VCard class="mt-4">
				<VSheet class="px-3" color="primary" tile>
					<VRow>
						<VCol cols={12} sm={4}>
							<KDatePicker
								showFieldArrows
								textFieldProps={{
									dark: true,
									flat: true,
									hideDetails: true,
									label: "Date*",
									prependIcon: null,
									prependInnerIcon: "event",
									soloInverted: true
								}}
								vModel={this.date}
							></KDatePicker>
						</VCol>

						<VCol cols={12} sm={8}>
							<KTextField
								clearable
								dark
								flat
								hideDetails
								label="Search"
								loading={this.$wait.is( "get schedules" )}
								prependInnerIcon="search"
								soloInverted
								vModel={this.search}
							></KTextField>
						</VCol>
					</VRow>
				</VSheet>

				<KSchedulesTable
					dense
					items={this.displayedSchedules}
					loading={this.$wait.is( "get schedules" )}
					onScheduleRemoved={this.onScheduleRemoved}
					weather
				>
					<template slot="title">
						{this.search
							? <span>Schedules for the search "{this.search}"</span>
							: <span>Schedules for {this.displayDate}</span>
						}
					</template>
				</KSchedulesTable>
			</VCard>
		</FullWidthLayout>;
	}
};
